import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import "./RobotMaster.css";
import authStore from "../services/authStore";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

export default function RobotMaster() {
  const [robotList, setRobotList] = useState([]);
  const [robotItemno, setItemno] = useState("");
  const [robotName, setRobotName] = useState("");
  const [category, setCategory] = useState("");
  const [editRobot, setEditRobot] = useState(null);
  const [editItemno, setEditItemno] = useState("");
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editStatus, setEditStatus] = useState("ACTIVE");
  const [addStatus, setAddStatus] = useState("ACTIVE");
  const [viewMode, setViewMode] = useState("LIST");

  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const token = authStore.getToken();

  const getUserIdFromToken = () => {
    const token = authStore.getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.user_id || decoded.userId || decoded.id;
    } catch (err) {
      console.error("Token decode error:", err);
      return null;
    }
  };

  // Normalize API response helper function
  const normalizeRobotData = (robot) => ({
    ID: robot.id || robot.ID,
    NAME: robot.robotname || robot.NAME || robot.name,
    ITEMNO: robot.itemno,
    CATEGORY: robot.category || robot.CATEGORY,
    STATUS: (robot.status || robot.STATUS || "INACTIVE").toUpperCase(),
  });

  // Fetch robot list with normalization
  const fetchRobotList = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/robotmaster/GetRobotList`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("API request failed");

      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : data?.data || data?.result || [];

      // Normalize all robots
      const formatted = list.map(normalizeRobotData);

      // Sort by ID in descending order
      const sortedList = formatted.sort((a, b) => b.ID - a.ID);
      setRobotList(sortedList);
    } catch (err) {
      console.error("API Error:", err.message);
      toast.error("Failed to fetch robot list");
    }
  };

  useEffect(() => {
    fetchRobotList();
  }, []);

  const handleSave = async () => {
    if (!robotName.trim()) {
      toast.warning("Please enter robot name");
      return;
    }

    if (!category.trim()) {
      toast.warning("Please select a category");
      return;
    }

    const newRobot = {
      itemno: robotItemno,
      robotname: robotName,
      category: category,
      unitprice: 0,
      status: addStatus === "ACTIVE" ? "Active" : "Inactive",
      createdby: getUserIdFromToken(),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/robotmaster/AddRobot`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newRobot),
      });

      if (!response.ok) throw new Error("Save failed");

      const data = await response.json();

      // Normalize the response to match UI structure
      const normalizedRobot = {
        ID: data.id || data.ID,
        ITEMNO: data.itemno || robotItemno,
        NAME: data.robotname || data.NAME || robotName,
        CATEGORY: category,
        STATUS: addStatus,
      };

      // Add new robot to list and sort descending
      setRobotList((prev) => {
        const updatedList = [normalizedRobot, ...prev];
        return updatedList.sort((a, b) => b.ID - a.ID);
      });

      // Reset form
      setItemno("");
      setRobotName("");
      setCategory("");
      setAddStatus("ACTIVE");
      setViewMode("LIST");

      toast.success(`Robot "${robotName}" added successfully`);
    } catch (err) {
      console.error("Save Error:", err.message);
      toast.error("Failed to save robot");
    }
  };

  const handleEdit = (robot) => {
    setEditRobot(robot);
    setEditItemno(robot.ITEMNO);
    setEditName(robot.NAME);
    setEditStatus(robot.STATUS);
    setEditCategory(robot.CATEGORY);
    setViewMode("EDIT");
  };

  const handleUpdate = async () => {
    if (!editName.trim()) {
      toast.warning("Please enter robot name");
      return;
    }

    if (!editCategory.trim()) {
      toast.warning("Please select a category");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/robotmaster/UpdateRobot/${editRobot.ID}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            itemno: editItemno,
            robotname: editName,
            category: editCategory,
            unitprice: 0,
            status: editStatus === "ACTIVE" ? "Active" : "Inactive",
            createdby: getUserIdFromToken(),
          }),
        },
      );

      if (!response.ok) throw new Error("Update failed");

      await response.json();

      // Update UI instantly
      setRobotList((prev) => {
        const updatedList = prev.map((item) =>
          item.ID === editRobot.ID
            ? {
              ...item,
              ITEMNO: editItemno,
              NAME: editName,
              CATEGORY: editCategory,
              STATUS: editStatus,
            }
            : item,
        );
        return updatedList.sort((a, b) => b.ID - a.ID);
      });

      setViewMode("LIST");
      setEditRobot(null);
      toast.success("Robot updated successfully");
    } catch (err) {
      console.error("Update Error:", err.message);
      toast.error("Failed to update robot");
    }
  };

  const handleStatusToggle = async (robot) => {
    const newStatus = robot.STATUS === "ACTIVE" ? "Inactive" : "Active";

    const createdby = getUserIdFromToken();

    try {
      const url = `${API_BASE_URL}/robotmaster/UpdateRobotStatus/${robot.ID}?id=${robot.ID}&status=${newStatus}&createdby=${createdby}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Status update failed");

      await response.json();

      // update UI
      setRobotList((prev) =>
        prev.map((item) =>
          item.ID === robot.ID
            ? { ...item, STATUS: newStatus.toUpperCase() }
            : item,
        ),
      );

      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      console.error("Status Error:", err.message);
      toast.error("Failed to update status");
    }
  };

  return (
    <>
      <Navbar />
      <div className="Pagename">
        <div>Robot Master</div>
      </div>

      <div className="Secondbar">
        {viewMode !== "LIST" && (
          <button
            className="back-circle-btn"
            onClick={() => setViewMode("LIST")}
            title="Back"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
        )}
        {viewMode === "LIST" && (
          <button
            className="plus-btn"
            onClick={() => {
              setViewMode("ADD");
              setItemno("");
              setRobotName("");
              setCategory("");
              setAddStatus("ACTIVE");
            }}
          >
            + Add Robot
          </button>
        )}
      </div>

      {viewMode === "LIST" && (
        <div className="table-container">
          <table className="robot-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Item No</th>
                <th>Robot Name</th>
                <th>Category</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {robotList.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    No data found
                  </td>
                </tr>
              ) : (
                robotList.map((item) => (
                  <tr key={item.ID}>
                    <td>{item.ID}</td>
                    <td>{item.ITEMNO}</td>
                    <td>{item.NAME}</td>
                    <td>{item.CATEGORY}</td>
                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.STATUS === "ACTIVE"}
                          onChange={() => handleStatusToggle(item)}
                        />
                        <span className="slider"></span>
                      </label>
                    </td>
                    <td className="action-col">
                      <i
                        className="fa-regular fa-pen-to-square fa-lg edit-icon"
                        onClick={() => handleEdit(item)}
                        title="Edit Robot"
                        style={{ cursor: "pointer" }}
                      ></i>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "ADD" && (
        <div className="addview">
          <h4>Add Robot</h4>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Item No</label>
            <input
              type="text"
              placeholder="Enter robot Item No"
              value={robotItemno}
              onChange={(e) => setItemno(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Name</label>
            <input
              type="text"
              placeholder="Enter robot name"
              value={robotName}
              onChange={(e) => setRobotName(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="Greeting">Greeting</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Delivery">Delivery</option>
              <option value="Advertisement">Advertisement</option>
              <option value="Accessories">Accessories</option>
            </select>
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <span>Status</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={addStatus === "ACTIVE"}
                onChange={() =>
                  setAddStatus(addStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE")
                }
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="popup-buttons">
            <button className="btn-save" onClick={handleSave}>
              Save
            </button>
            <button
              className="btn-cancel"
              onClick={() => {
                setItemno("");
                setRobotName("");
                setCategory("");
                setAddStatus("ACTIVE");
                setViewMode("LIST");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {viewMode === "EDIT" && (
        <div className="addview">
          <h4>Edit Robot</h4>

          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Item No</span>
            <input
              type="text"
              placeholder="Enter Item No"
              value={editItemno}
              onChange={(e) => setEditItemno(e.target.value)}
            />
          </div>

          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Name</span>
            <input
              type="text"
              placeholder="Enter robot name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <span>Category</span>
            <select
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="Greeting">Greeting</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Delivery">Delivery</option>
              <option value="Advertisement">Advertisement</option>
              <option value="Accessories">Accessories</option>
            </select>
          </div>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <span>Status</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={editStatus === "ACTIVE"}
                onChange={() =>
                  setEditStatus(editStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE")
                }
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="popup-buttons">
            <button className="btn-save" onClick={handleUpdate}>
              Update
            </button>
            <button
              className="btn-cancel"
              onClick={() => {
                setViewMode("LIST");
                setEditRobot(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
